// Frontend-Pendant zum Berechtigungs-System im Backend.
// Spiegelt /app/backend/security/permissions.py.

export const ROLES = {
  DEVELOPER: 'DEVELOPER',
  WACHLEITUNG: 'WACHLEITUNG',
  WACHLEITUNG_VERTRETUNG: 'WACHLEITUNG_VERTRETUNG',
  WACHABTEILUNGSLEITUNG: 'WACHABTEILUNGSLEITUNG',
  ADMIN: 'ADMIN',
  MITARBEITER: 'MITARBEITER',
};

export const PERMISSIONS = {
  EINTEILUNG_BEARBEITEN: 'EINTEILUNG_BEARBEITEN',
  POOL_KLEIDUNG_VERWALTEN: 'POOL_KLEIDUNG_VERWALTEN',
  PRAKTIKANTEN_VERWALTEN: 'PRAKTIKANTEN_VERWALTEN',
  ATEMSCHUTZ_VERWALTEN: 'ATEMSCHUTZ_VERWALTEN',
};

const GLOBAL_ROLES = new Set([
  ROLES.DEVELOPER, ROLES.WACHLEITUNG, ROLES.WACHLEITUNG_VERTRETUNG,
]);

const WAL_ROLES = new Set([ROLES.WACHABTEILUNGSLEITUNG, ROLES.ADMIN]);
const LEITUNGS_ROLES = new Set([
  ROLES.DEVELOPER, ROLES.WACHLEITUNG, ROLES.WACHLEITUNG_VERTRETUNG,
  ROLES.WACHABTEILUNGSLEITUNG, ROLES.ADMIN,
]);

export const isGlobalRole = (rolle) => GLOBAL_ROLES.has(rolle);
export const isLeitung = (rolle) => LEITUNGS_ROLES.has(rolle);

export const hasPermission = (user, permission) => {
  if (!user) return false;
  if (isGlobalRole(user.rolle)) return true;
  const list = user.zusatzberechtigungen || [];
  return list.includes(permission);
};

export const isSameAbteilung = (user, abteilung_id) => {
  if (!user) return false;
  if (isGlobalRole(user.rolle)) return true;
  return !!abteilung_id && user.abteilung_id === abteilung_id;
};

// Wer darf die Einteilung in einer Wachabteilung bearbeiten?
export const canEditEinteilung = (user, target_abteilung_id) => {
  if (!user) return false;
  if (isGlobalRole(user.rolle)) return true;
  if (WAL_ROLES.has(user.rolle)) return isSameAbteilung(user, target_abteilung_id);
  if (hasPermission(user, PERMISSIONS.EINTEILUNG_BEARBEITEN)) {
    return isSameAbteilung(user, target_abteilung_id);
  }
  return false;
};

// Wer darf einen User mit Rolle/Abteilung bearbeiten?
export const canEditUser = (actor, target_rolle, target_abteilung_id) => {
  if (!actor) return false;
  if (actor.rolle === ROLES.DEVELOPER) return true;
  if (actor.rolle === ROLES.WACHLEITUNG || actor.rolle === ROLES.WACHLEITUNG_VERTRETUNG) {
    return target_rolle !== ROLES.DEVELOPER;
  }
  if (actor.rolle === ROLES.WACHABTEILUNGSLEITUNG) {
    if (target_rolle === ROLES.ADMIN || target_rolle === ROLES.MITARBEITER) {
      return isSameAbteilung(actor, target_abteilung_id);
    }
    return false;
  }
  if (actor.rolle === ROLES.ADMIN) {
    if (target_rolle === ROLES.MITARBEITER) {
      return isSameAbteilung(actor, target_abteilung_id);
    }
    return false;
  }
  return false;
};

export const canDeleteUser = (actor, target_rolle, target_abteilung_id) => {
  if (!actor) return false;
  if (actor.rolle === ROLES.DEVELOPER) return true;
  if (actor.rolle === ROLES.WACHLEITUNG || actor.rolle === ROLES.WACHLEITUNG_VERTRETUNG) {
    return target_rolle !== ROLES.DEVELOPER;
  }
  if (actor.rolle === ROLES.WACHABTEILUNGSLEITUNG) {
    return (target_rolle === ROLES.ADMIN || target_rolle === ROLES.MITARBEITER)
      && isSameAbteilung(actor, target_abteilung_id);
  }
  return false;
};

export const canResetPassword = canEditUser;

export const canSetZusatzberechtigungen = (actor) => {
  if (!actor) return false;
  return [
    ROLES.DEVELOPER, ROLES.WACHLEITUNG, ROLES.WACHLEITUNG_VERTRETUNG,
    ROLES.WACHABTEILUNGSLEITUNG,
  ].includes(actor.rolle);
};

export const canSetTags = (actor) => {
  if (!actor) return false;
  // Nur echte Leitungsrollen (inkl. Wachabteilungsleitung) dürfen Tags anlegen/löschen.
  // ADMIN und MITARBEITER dürfen Tags NUR zuweisen, aber nicht verwalten.
  return [
    ROLES.DEVELOPER,
    ROLES.WACHLEITUNG,
    ROLES.WACHLEITUNG_VERTRETUNG,
    ROLES.WACHABTEILUNGSLEITUNG,
  ].includes(actor.rolle);
};

export const canCreateFahrzeug = (actor) => {
  if (!actor) return false;
  return [ROLES.DEVELOPER, ROLES.WACHLEITUNG, ROLES.WACHLEITUNG_VERTRETUNG].includes(actor.rolle);
};

export const canEditFahrzeug = (actor) => {
  if (!actor) return false;
  return LEITUNGS_ROLES.has(actor.rolle);
};
